/**
 * Error handling types and utilities for the MATIC API client system
 */

/**
 * Error categories for classification
 */
export enum ErrorCategory {
  Network = 'network',
  Validation = 'validation',
  Server = 'server',
  Client = 'client',
  Authentication = 'authentication',
}

/**
 * Error context interface for preserving error details
 */
export type ErrorContext = {
  originalError: unknown;
  endpoint?: string;
  timestamp: Date;
  category: ErrorCategory;
  userMessage: string;
};

/**
 * Enhanced API Error class with categorization
 */
export class EnhancedApiError extends Error {
  status: number;
  data?: unknown;
  category: ErrorCategory;
  context: ErrorContext;

  constructor(
    message: string,
    status: number,
    data?: unknown,
    category?: ErrorCategory,
    context?: Partial<ErrorContext>
  ) {
    super(message);
    this.name = 'EnhancedApiError';
    this.status = status;
    this.data = data;
    this.category = category ?? getHttpStatusCategory(status);

    this.context = {
      originalError: this,
      timestamp: new Date(),
      category: this.category,
      userMessage: message,
      ...context,
    };
  }
}

/**
 * Type guard to check if error is an ApiError instance
 */
export function isApiError(
  error: unknown
): error is {status: number; message: string; data?: unknown} {
  return (
    error !== null &&
    error !== undefined &&
    typeof error === 'object' &&
    'status' in error &&
    'message' in error &&
    typeof (error as {status: unknown}).status === 'number' &&
    typeof (error as {message: unknown}).message === 'string'
  );
}

/**
 * Classify error into appropriate category
 */
export function classifyError(error: unknown): ErrorCategory {
  // Handle network errors
  if (isNetworkError(error)) {
    return ErrorCategory.Network;
  }

  // Handle ApiError instances and objects with status property
  if (isApiError(error)) {
    return getHttpStatusCategory(error.status);
  }

  // Handle objects with status property (even without message)
  if (error && typeof error === 'object' && 'status' in error) {
    const {status} = error as {status: unknown};
    if (typeof status === 'number') {
      return getHttpStatusCategory(status);
    }
  }

  // Handle regular Error instances
  if (error instanceof Error) {
    // Check for specific error patterns
    const message = error.message.toLowerCase();

    if (
      message.includes('network') ||
      message.includes('timeout') ||
      message.includes('fetch')
    ) {
      return ErrorCategory.Network;
    }

    if (message.includes('unauthorized') || message.includes('forbidden')) {
      return ErrorCategory.Authentication;
    }

    if (message.includes('validation') || message.includes('invalid')) {
      return ErrorCategory.Validation;
    }
  }

  // Default to client error for unknown errors
  return ErrorCategory.Client;
}

/**
 * Check if error is a network-related error
 */
export function isNetworkError(error: unknown): boolean {
  // Handle TypeError with "Failed to fetch" (common network error)
  if (error instanceof TypeError && error.message === 'Failed to fetch') {
    return true;
  }

  // Handle timeout errors
  if (error instanceof Error) {
    const message = error.message.toLowerCase();
    if (
      message.includes('timeout') ||
      message.includes('network error') ||
      message.includes('connection') ||
      message.includes('fetch')
    ) {
      return true;
    }
  }

  // Handle ApiError with status 0 (network failure)
  if (isApiError(error) && error.status === 0) {
    return true;
  }

  return false;
}

/**
 * Map HTTP status codes to error categories
 */
export function getHttpStatusCategory(status: number): ErrorCategory {
  // Network errors (no status or 0)
  if (status === 0) {
    return ErrorCategory.Network;
  }

  // Authentication errors
  if (status === 401 || status === 403) {
    return ErrorCategory.Authentication;
  }

  // Validation errors
  if (status === 400 || status === 413 || status === 415 || status === 422) {
    return ErrorCategory.Validation;
  }

  // Client errors (other 4xx)
  if (status >= 400 && status < 500) {
    return ErrorCategory.Client;
  }

  // Server errors (5xx)
  if (status >= 500 && status < 600) {
    return ErrorCategory.Server;
  }

  // Unknown status codes default to client error
  return ErrorCategory.Client;
}

/**
 * Generate user-friendly error message based on error and category
 */
export function generateUserMessage(
  error: unknown,
  category: ErrorCategory
): string {
  // Handle specific error messages first
  if (isApiError(error)) {
    const message = error.message.toLowerCase();

    // File upload specific errors
    if (error.status === 413 || message.includes('too large')) {
      return 'File is too large. Please choose a smaller file.';
    }

    if (
      error.status === 415 ||
      message.includes('unsupported') ||
      message.includes('file type')
    ) {
      return 'File type not supported. Please choose a different file.';
    }

    // Custom server error messages
    if (error.status >= 500 && message.includes('upload failed')) {
      return 'Upload failed. Please try again.';
    }
  }

  // Handle by category
  switch (category) {
    case ErrorCategory.Network: {
      return 'Connection problem. Please check your internet and try again.';
    }

    case ErrorCategory.Authentication: {
      return 'You are not authorized to perform this action. Please log in and try again.';
    }

    case ErrorCategory.Validation: {
      return 'Invalid file or document information. Please check your input.';
    }

    case ErrorCategory.Server: {
      return 'Server error. Please try again later.';
    }

    case ErrorCategory.Client: {
      return 'An error occurred. Please try again.';
    }
  }
}

/**
 * Create comprehensive error context
 */
export function createErrorContext(
  error: unknown,
  endpoint?: string
): ErrorContext {
  const category = classifyError(error);
  const userMessage = generateUserMessage(error, category);

  return {
    originalError: error,
    endpoint,
    timestamp: new Date(),
    category,
    userMessage,
  };
}

/**
 * Preserve error context without losing information
 */
export function preserveErrorContext(error: unknown): ErrorContext {
  return createErrorContext(error);
}

/**
 * Create enhanced error with context
 */
export function createEnhancedError(
  error: unknown,
  endpoint?: string
): Error & {originalError: unknown; context: ErrorContext} {
  const context = createErrorContext(error, endpoint);
  const enhancedError = new Error(context.userMessage) as Error & {
    originalError: unknown;
    context: ErrorContext;
  };

  enhancedError.originalError = error;
  enhancedError.context = context;

  return enhancedError;
}

/**
 * Extract the most descriptive error message from an API error.
 *
 * Handles EnhancedApiError (which stores the raw API response in `data`)
 * and falls back through several message sources to find the best one.
 * This is more robust than using `error.message` directly, which can
 * sometimes be a generic HTTP status text (e.g., "400 Bad Request").
 */
export function getApiErrorMessage(error: unknown): string {
  if (error && typeof error === 'object') {
    const errorObj = error as {
      message?: string;
      data?: {detail?: unknown; message?: unknown};
    };

    // Check error.data.detail first (FastAPI response body preserved by EnhancedApiError)
    if (errorObj.data && typeof errorObj.data === 'object') {
      const {detail, message: dataMessage} = errorObj.data;
      if (typeof detail === 'string' && detail.trim()) {
        return detail.trim();
      }

      if (typeof dataMessage === 'string' && dataMessage.trim()) {
        return dataMessage.trim();
      }
    }

    // Fall back to error.message if it's not a generic HTTP status text
    if (errorObj.message?.trim() && !/^\d{3}\s/.test(errorObj.message)) {
      return errorObj.message;
    }

    // Use error.message even if generic (last resort for object errors)
    if (errorObj.message) {
      return errorObj.message;
    }
  }

  if (error instanceof Error) {
    return error.message;
  }

  return 'An unexpected error occurred';
}
